'use client'

import { useSignals } from '@preact/signals-react/runtime'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import type { Product } from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { trackImpressionProduct } from '@susu/headless-commerce/utils/tracking/tracking'
import { isUndefined } from '@susu/undefined'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import {
  fallbackListName,
  listNameMap,
} from '../CMSPageSections/ShopCollectionBlockSection/listNameMap'
import type { ProductTrackingData } from './ClientProductCardImageLink'
import styles from './ProductCard.module.scss'

export type ClientProductCardProps = {
  product: Product
  children?: ReactNode
  tracking: Omit<ProductTrackingData, 'list'>
}

export default function ClientProductCard({
  product,
  tracking,
  children,
}: Readonly<ClientProductCardProps>) {
  useSignals()
  const pageType = usePageType()
  const country = useCountry()
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    ;(async () => {
      if (isUndefined(product)) {
        return
      }

      trackImpressionProduct({
        elementRef: cardRef,
        currencyCode: country.ecommerce.currencyCode,
        tracking: {
          ...tracking,
          list: listNameMap[pageType] || fallbackListName,
        },
        product,
        siteId: country.siteID,
      })
    })()
  }, [
    country.ecommerce.currencyCode,
    country.siteID,
    pageType,
    product,
    tracking,
    tracking.listId,
    tracking.position,
  ])

  return (
    <div
      ref={cardRef}
      className={styles['product-card__wrapper']}
      data-testid="product-card"
    >
      {children}
    </div>
  )
}
